/**
 * HBSDate — Pure JavaScript implementation of the RetailCLIQ HBS Date system.
 *
 * CONTEXT:
 *   All date columns in the RC26 MSSQL databases (VchDate, CreatedDate,
 *   ModifiedDate, DeletedDate, StartDate, EndDate, IntBusinessDate, etc.)
 *   are stored as INTEGER values in the HBS format — NOT as SQL DATE/DATETIME.
 *
 *   The SQL Server UDFs are:
 *     dbo.englishHBS('dd-MM-yyyy')   → INT  (Gregorian → HBS)
 *     dbo.ConvertHBSDate(INT)        → 'dd-MMM-yyyy' string
 *
 * ALGORITHM (reverse-engineered from live DB, verified against 15 data points):
 *   HBS_BASE = 20833     (= englishHBS('01-01-2000'))
 *   YEAR_FACTOR = 416    (= 13 × 32 per year)
 *   MONTH_FACTOR = 32    (32 slots per month)
 *
 *   Encode: HBS = 20833 + (year − 2000) × 416 + (month − 1) × 32 + (day − 1)
 *   Decode: n = HBS − 20833
 *           year  = Math.floor(n / 416) + 2000
 *           month = Math.floor((n % 416) / 32) + 1
 *           day   = (n % 416) % 32 + 1
 *
 * USAGE:
 *   import HBSDate from '@/utils/HBSDate';
 *
 *   // Convert a JS Date → HBS integer (to store in DB)
 *   const hbs = HBSDate.fromDate(new Date());           // → e.g. 31783
 *
 *   // Convert HBS integer → JS Date (from DB)
 *   const jsDate = HBSDate.toDate(31783);               // → Date(2026-05-07)
 *
 *   // Today as HBS integer
 *   const todayHBS = HBSDate.today();                   // → 31783
 *
 *   // Business date from GlobalStore
 *   const bizHBS = HBSDate.businessDate();
 *
 *   // Display-formatted string from HBS integer
 *   const str = HBSDate.format(31329);                  // → '01-Apr-2025'
 *   const iso = HBSDate.toISO(31329);                   // → '2025-04-01'
 *
 *   // Validate a date string
 *   HBSDate.isValid('07-05-2026');                      // → true
 *   HBSDate.isValid('32-01-2026');                      // → false
 *
 *   // Parse display string → HBS integer
 *   const hbs2 = HBSDate.fromString('01-04-2025');      // → 31329
 *   const hbs3 = HBSDate.fromString('2025-04-01');      // → 31329 (ISO format)
 *
 *   // Fiscal year bounds as HBS integers (from GlobalStore)
 *   const fyStart = HBSDate.fyStart();
 *   const fyEnd   = HBSDate.fyEnd();
 */

const HBS_BASE    = 20833;  // englishHBS('01-01-2000')
const YEAR_FACTOR = 416;    // 13 * 32 slots per year
const MONTH_FACTOR = 32;    // 32 slots per month

// Month names matching ConvertHBSDate output ('dd-MMM-yyyy')
const MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_NAMES_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Display format used across the ERP (mirrors strDateFormat in GlobalFunctions_UI)
const DEFAULT_FORMAT = 'DD-MMM-YYYY';  // e.g. '01-Apr-2025'

// ─── Core encode / decode ─────────────────────────────────────────────────────

/**
 * Encode year, month, day → HBS integer.
 * This is the pure-JS equivalent of dbo.englishHBS(date_string).
 */
function encode(year, month, day) {
  if (year < 1900 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) {
    return 0; // invalid
  }
  return HBS_BASE + (year - 2000) * YEAR_FACTOR + (month - 1) * MONTH_FACTOR + (day - 1);
}

/**
 * Decode HBS integer → { year, month, day }.
 * This is the pure-JS equivalent of dbo.ConvertHBSDate(hbs_int).
 */
function decode(hbs) {
  if (!hbs || hbs <= 0) return null;
  const n     = hbs - HBS_BASE;
  const year  = Math.floor(n / YEAR_FACTOR) + 2000;
  const rem   = n % YEAR_FACTOR;
  const month = Math.floor(rem / MONTH_FACTOR) + 1;
  const day   = (rem % MONTH_FACTOR) + 1;
  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

// ─── Main HBSDate object ──────────────────────────────────────────────────────

const HBSDate = {

  // ── Encoding (→ HBS integer, for DB INSERT/UPDATE) ────────────────────────

  /** JS Date object → HBS integer */
  fromDate(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return 0;
    return encode(date.getFullYear(), date.getMonth() + 1, date.getDate());
  },

  /**
   * Date string → HBS integer.
   * Accepts:
   *   'dd-MM-yyyy'    → '07-05-2026'
   *   'dd-MMM-yyyy'   → '07-May-2026'
   *   'yyyy-MM-dd'    → '2026-05-07'  (ISO)
   *   'dd/MM/yyyy'    → '07/05/2026'
   */
  fromString(str) {
    if (!str) return 0;
    const d = HBSDate.parse(str);
    return d ? encode(d.year, d.month, d.day) : 0;
  },

  /** Today's date as HBS integer */
  today() {
    const d = new Date();
    return encode(d.getFullYear(), d.getMonth() + 1, d.getDate());
  },

  /** Business date from GlobalStore (falls back to today) */
  businessDate() {
    try {
      const GlobalStore = require('./GlobalStore').default;
      const { GK }      = require('../store/GlobalKeys');
      const intHBS = GlobalStore.get(GK.INT_BUSINESS_DATE);
      if (intHBS && intHBS > 0) return intHBS;
      const isoDate = GlobalStore.get(GK.BUSINESS_DATE);
      if (isoDate) return HBSDate.fromString(isoDate);
    } catch (_) { /* outside React / store not ready */ }
    return HBSDate.today();
  },

  /** Fiscal year start as HBS integer */
  fyStart() {
    try {
      const GlobalStore = require('./GlobalStore').default;
      const { GK }      = require('../store/GlobalKeys');
      return GlobalStore.get(GK.INT_FY_START) || HBSDate.fromString(GlobalStore.get(GK.FY_START_DATE) || '');
    } catch (_) { return 0; }
  },

  /** Fiscal year end as HBS integer */
  fyEnd() {
    try {
      const GlobalStore = require('./GlobalStore').default;
      const { GK }      = require('../store/GlobalKeys');
      return GlobalStore.get(GK.INT_FY_END) || HBSDate.fromString(GlobalStore.get(GK.FY_END_DATE) || '');
    } catch (_) { return 0; }
  },

  // ── Decoding (← HBS integer, from DB SELECT) ─────────────────────────────

  /** HBS integer → JS Date object */
  toDate(hbs) {
    const d = decode(hbs);
    if (!d) return null;
    return new Date(d.year, d.month - 1, d.day);
  },

  /** HBS integer → ISO string 'YYYY-MM-DD' */
  toISO(hbs) {
    const d = decode(hbs);
    if (!d) return '';
    return `${d.year}-${String(d.month).padStart(2,'0')}-${String(d.day).padStart(2,'0')}`;
  },

  /**
   * HBS integer → formatted display string.
   * format options: 'DD-MMM-YYYY' (default), 'DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'
   */
  format(hbs, fmt) {
    const d = decode(hbs);
    if (!d) return '';
    const dd   = String(d.day).padStart(2, '0');
    const mm   = String(d.month).padStart(2, '0');
    const mmm  = MONTH_NAMES_SHORT[d.month - 1];
    const yyyy = String(d.year);
    const f    = fmt || DEFAULT_FORMAT;
    return f
      .replace('DD', dd)
      .replace('MMM', mmm)
      .replace('MM', mm)
      .replace('YYYY', yyyy)
      .replace('YY', yyyy.slice(2));
  },

  /** HBS integer → components { year, month, day } */
  components(hbs) { return decode(hbs); },

  // ── Parsing ───────────────────────────────────────────────────────────────

  /**
   * Parse any date string into { year, month, day }.
   * Returns null on invalid input.
   */
  parse(str) {
    if (!str) return null;
    str = String(str).trim();

    // ISO format: YYYY-MM-DD
    let m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) return { year: +m[1], month: +m[2], day: +m[3] };

    // dd-MM-yyyy or dd/MM/yyyy
    m = str.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
    if (m) return { year: +m[3], month: +m[2], day: +m[1] };

    // dd-MMM-yyyy (e.g. '01-Apr-2025')
    m = str.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
    if (m) {
      const month = MONTH_NAMES_SHORT.findIndex(n => n.toLowerCase() === m[2].toLowerCase()) + 1;
      if (month > 0) return { year: +m[3], month, day: +m[1] };
    }

    // dd-MMMM-yyyy (e.g. '01-April-2025')
    m = str.match(/^(\d{1,2})-([A-Za-z]+)-(\d{4})$/);
    if (m) {
      const month = MONTH_NAMES_LONG.findIndex(n => n.toLowerCase() === m[2].toLowerCase()) + 1;
      if (month > 0) return { year: +m[3], month, day: +m[1] };
    }

    return null;
  },

  // ── Validation ────────────────────────────────────────────────────────────

  /** Returns true if the string/HBS integer represents a valid date */
  isValid(input) {
    if (typeof input === 'number') return decode(input) !== null;
    const d = HBSDate.parse(String(input));
    if (!d) return false;
    // Range check
    if (d.year < 1900 || d.year > 2200) return false;
    if (d.month < 1 || d.month > 12)   return false;
    if (d.day < 1 || d.day > 31)       return false;
    return true;
  },

  // ── Arithmetic ────────────────────────────────────────────────────────────

  /** Add N calendar days to an HBS integer (use for date arithmetic) */
  addDays(hbs, days) {
    const d = HBSDate.toDate(hbs);
    if (!d) return 0;
    d.setDate(d.getDate() + days);
    return HBSDate.fromDate(d);
  },

  /** Difference in calendar days between two HBS integers */
  diffDays(hbs1, hbs2) {
    const d1 = HBSDate.toDate(hbs1);
    const d2 = HBSDate.toDate(hbs2);
    if (!d1 || !d2) return 0;
    return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  },

  /** Compare: returns -1, 0, or 1 */
  compare(hbs1, hbs2) {
    if (hbs1 < hbs2) return -1;
    if (hbs1 > hbs2) return 1;
    return 0;
  },

  /** Is hbs within [fyStart, fyEnd]? */
  isInFY(hbs) {
    const s = HBSDate.fyStart();
    const e = HBSDate.fyEnd();
    return s > 0 && e > 0 && hbs >= s && hbs <= e;
  },

  // ── SQL helpers ───────────────────────────────────────────────────────────

  /**
   * Returns the SQL expression to convert a JS date to HBS for use in queries.
   * Usage: executeQuery(`INSERT INTO Vouchers ... WHERE VchDate = @vchDate`, { vchDate: HBSDate.fromDate(d) })
   * Note: You pass the integer directly as a parameter — no SQL function needed on the client side.
   */
  sqlParam(date) { return HBSDate.fromDate(date instanceof Date ? date : new Date(date)); },

  // ── Constants ─────────────────────────────────────────────────────────────
  HBS_BASE,
  YEAR_FACTOR,
  MONTH_FACTOR,
  DEFAULT_FORMAT,
  MONTH_NAMES_SHORT,
  MONTH_NAMES_LONG,
};

export default HBSDate;

// Named exports for tree-shaking
export { encode as hbsEncode, decode as hbsDecode };
